// Read-only billing card for a plan a CLINIC pays for (Wave 2b, §11.7a).
//
// A covered doctor buys nothing and can cancel nothing: the row lives on the
// clinic's parent subscription, so `/api/account/billing` (which lists the
// rows the CALLER pays for) never shows it and `/api/account/subscriptions/
// :id/inventory` would refuse him. Everything here comes from
// GET /api/account/provider/plan-rows, and there is deliberately no
// Change/Cancel action — only the sentence that says who to ask.

import { Trans, useLingui } from "@lingui/react/macro";
import { AlertTriangle, Building2 } from "lucide-react";

import type { ProviderPlanRow } from "@/components/billing/provider-plan-rows";
import { Badge } from "@/components/ui/badge";
import { tLocalized } from "@/types/localized-string";

export function CoveragePlanCard({ row }: { row: ProviderPlanRow }) {
  const { i18n } = useLingui();
  const locale = i18n.locale;

  const planLabel = (row.planName ? tLocalized(row.planName, locale) : "") || row.planLabel;
  // A coverage row whose parent is not a facility (an admin grant that spends a
  // seat off a provider/user row) has no clinic to name. Saying "offered by your
  // clinic" there would be a lie, and the API deliberately sends no facilityId.
  const fromFacility = row.parentEntityType === "facility";
  const clinicName = (fromFacility && row.context?.facilityName) || "";
  const until = row.currentPeriodEnd ?? row.endsAt;
  const untilLabel = until
    ? new Date(until).toLocaleDateString(locale, {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

  return (
    <div className="bg-card border-border overflow-hidden rounded-xl border">
      <div className="bg-muted/30 border-border flex flex-wrap items-center justify-between gap-3 border-b p-5">
        <div className="flex items-center gap-3">
          <div className="bg-accent-soft text-accent-strong flex size-10 items-center justify-center rounded-lg">
            <Building2 className="size-5" aria-hidden />
          </div>
          <div>
            <div className="text-foreground text-base font-semibold">
              {clinicName ? (
                <Trans>
                  {planLabel} · offered by {clinicName}
                </Trans>
              ) : fromFacility ? (
                <Trans>{planLabel} · offered by your clinic</Trans>
              ) : (
                <Trans>{planLabel} · offered by another subscription</Trans>
              )}
            </div>
            <div className="text-muted-foreground text-xs">
              {clinicName && (
                <>
                  <Trans>at {clinicName}</Trans>
                  {untilLabel ? " · " : ""}
                </>
              )}
              {untilLabel && <Trans>Valid until {untilLabel} · renewed by the clinic</Trans>}
            </div>
          </div>
        </div>
        {/* Deliberately not a button: nothing here is the doctor's to change. */}
        <Badge variant="secondary">
          {fromFacility ? (
            <Trans>Managed by the clinic</Trans>
          ) : (
            <Trans>Managed by the payer</Trans>
          )}
        </Badge>
      </div>

      <div className="space-y-2 p-5">
        <p className="text-muted-foreground text-xs">
          {fromFacility ? (
            <Trans>
              You are not billed for this plan and there is nothing to cancel. Want to pay for it
              yourself? Ask the clinic to turn coverage off.
            </Trans>
          ) : (
            <Trans>
              You are not billed for this plan and there is nothing to cancel. It comes from a
              subscription someone else pays for.
            </Trans>
          )}
        </p>
        {row.status === "past_due" && (
          <p className="text-warning flex items-center gap-1.5 text-xs">
            <AlertTriangle className="size-3.5 shrink-0" aria-hidden />
            <Trans>The last payment on this plan failed — the coverage may end soon.</Trans>
          </p>
        )}
      </div>
    </div>
  );
}
